import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { mxn } from '../formato';
import { usePuede } from '../permisos';
import { ESTADO_PIEZA, type Pieza } from './Piezas';

/**
 * Mostrador de refacciones (arquitectura 2.15, rol mostrador): buscar una pieza por nombre,
 * número de parte o auto, apartarla para un cliente y venderla con su precio real. Sin costos: el
 * mostrador ve el precio de lista y la ubicación, no el costo asignado de la pieza.
 */
export function Mostrador() {
  const api = useApi();
  const permitido = usePuede();
  const [buscar, setBuscar] = useState('');
  const [texto, setTexto] = useState('');
  const consulta = buscar
    ? `/inventory/piezas?buscar=${encodeURIComponent(buscar)}`
    : '/inventory/piezas?estado=en_inventario';
  const lista = useConsulta<Pieza[]>(consulta);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const enviar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBuscar(texto.trim());
  };
  const cambiar = async (p: Pieza, cuerpo: Record<string, unknown>, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await api('POST', `/inventory/piezas/${p.id}/estado`, cuerpo);
      setAviso(mensaje);
      lista.recargar();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const vender = async (p: Pieza) => {
    const precio = window.prompt(
      `Precio de venta de «${p.nombre}» (lista ${mxn(p.precioListaMxn)})`,
      p.precioListaMxn,
    );
    if (!precio) return;
    await cambiar(
      p,
      { estado: 'vendida', precioVentaMxn: precio.trim() },
      `Vendida ${p.nombre} en ${mxn(precio.trim())}`,
    );
  };
  const apartar = async (p: Pieza) => {
    const para = window.prompt('¿Para quién se aparta? (nombre o teléfono del cliente)');
    if (!para || para.trim().length < 2) return;
    await cambiar(p, { estado: 'apartada', apartadaPara: para.trim() }, `Apartada ${p.nombre}`);
  };
  const liberar = (p: Pieza) =>
    cambiar(p, { estado: 'en_inventario' }, `${p.nombre} regresó al inventario`);
  const entregar = (p: Pieza) => cambiar(p, { estado: 'entregada' }, `Entregada ${p.nombre}`);

  const disponibles = (lista.datos ?? []).filter((p) =>
    ['en_inventario', 'apartada', 'vendida'].includes(p.estado),
  );
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Mostrador</h1>
          <div className="subtitulo">
            Busca la pieza, dile al cliente dónde está y véndela o apártala
          </div>
        </div>
        <div className="relleno" />
        <Link className="boton" to="/piezas">
          Inventario completo
        </Link>
      </div>
      <form className="seccion cuerpo acciones" onSubmit={enviar}>
        <input
          type="search"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Puerta delantera, alternador, 3VWFE21C…, número de parte"
          aria-label="Buscar pieza"
          style={{ flex: 1, minWidth: 200 }}
        />
        <button className="boton primario">Buscar</button>
        {buscar && (
          <button
            type="button"
            className="boton"
            onClick={() => {
              setTexto('');
              setBuscar('');
            }}
          >
            Ver disponibles
          </button>
        )}
      </form>
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      <section className="seccion">
        {lista.error && <p className="error">{lista.error}</p>}
        <div className="tarjetas">
          {disponibles.map((p) => (
            <article key={p.id} className="tarjeta">
              <div className="rotulo">{p.categoria}</div>
              <strong>{p.nombre}</strong>
              <div className="nota">
                <Link to={`/vehiculos/${p.vehiculoId}`}>{p.vin}</Link>
                {p.numeroParte ? ` · nº ${p.numeroParte}` : ''}
                {p.grado ? ` · grado ${p.grado}` : ''}
              </div>
              <div className="nota">
                Ubicación: <strong>{p.ubicacion ?? 'sin asignar'}</strong> · código {p.codigo}
              </div>
              <div className="cifra">{mxn(p.precioListaMxn)}</div>
              <span className="etiqueta">{ESTADO_PIEZA[p.estado] ?? p.estado}</span>
              {permitido('piezas.estado') && (
                <div className="acciones">
                  {p.estado === 'en_inventario' && (
                    <>
                      <button className="boton primario" onClick={() => void vender(p)}>
                        Vender
                      </button>
                      <button className="boton" onClick={() => void apartar(p)}>
                        Apartar
                      </button>
                    </>
                  )}
                  {p.estado === 'apartada' && (
                    <>
                      <button className="boton primario" onClick={() => void vender(p)}>
                        Vender
                      </button>
                      <button className="boton" onClick={() => void liberar(p)}>
                        Liberar
                      </button>
                    </>
                  )}
                  {p.estado === 'vendida' && (
                    <button className="boton" onClick={() => void entregar(p)}>
                      Entregar
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
        {!lista.cargando && disponibles.length === 0 && (
          <p className="vacio">
            {buscar ? 'Ninguna pieza coincide.' : 'No hay piezas en inventario.'}
          </p>
        )}
      </section>
    </>
  );
}
